import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Calendar, Save, Check, X, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AttendancePage() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [students, setStudents] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [statuses, setStatuses] = useState({}); // {student_id: "present"|"absent"|"late"}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/classes").then(({ data }) => setClasses(data)); }, []);

  useEffect(() => {
    if (!classId) { setStudents([]); return; }
    setLoading(true);
    api.get(`/classes/${classId}/students`)
      .then(({ data }) => {
        setStudents(data);
        // Marquer tous présents par défaut
        const init = {};
        data.forEach(s => { init[s.id] = "present"; });
        setStatuses(init);
      })
      .finally(() => setLoading(false));
  }, [classId]);

  const setStatus = (sid, status) => setStatuses(st => ({ ...st, [sid]: status }));
  const markAll = (status) => {
    const newSt = {};
    students.forEach(s => { newSt[s.id] = status; });
    setStatuses(newSt);
  };

  const submit = async () => {
    const items = students.map(s => ({ student_id: s.id, status: statuses[s.id] || "present", comment: "" }));
    if (items.length === 0) { toast.error("Aucun élève"); return; }
    setSaving(true);
    try {
      await api.post("/attendance/bulk", { class_id: classId, date, items });
      const absents = items.filter(i => i.status === "absent").length;
      const lates = items.filter(i => i.status === "late").length;
      toast.success(`Présences enregistrées : ${items.length - absents - lates} présent(s), ${absents} absent(s), ${lates} retard(s)`);
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const counts = {
    present: students.filter(s => statuses[s.id] === "present").length,
    absent: students.filter(s => statuses[s.id] === "absent").length,
    late: students.filter(s => statuses[s.id] === "late").length,
  };

  return (
    <div className="space-y-5" data-testid="page-attendance">
      <div>
        <div className="chip mb-2"><Calendar className="h-3 w-3" /><span>Appel</span></div>
        <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Présences & absences</h2>
        <p className="text-sm text-slate-500 mt-1">Faites l&apos;appel rapidement pour toute une classe.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 grid md:grid-cols-3 gap-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Classe</label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="w-full" data-testid="attendance-class-select"><SelectValue placeholder="— Choisir —" /></SelectTrigger>
            <SelectContent>
              {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.level}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm h-9" data-testid="attendance-date-input" />
        </div>
        <div className="flex items-end gap-2">
          <button onClick={() => markAll("present")} disabled={students.length === 0} className="flex-1 text-xs px-3 py-2 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 font-semibold" data-testid="mark-all-present">
            Tous présents
          </button>
        </div>
      </div>

      {loading && <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>}

      {!loading && classId && students.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center text-sm text-yellow-800">
          Aucun élève dans cette classe.
        </div>
      )}

      {students.length > 0 && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center" data-testid="stat-present">
              <div className="text-xs uppercase tracking-wider text-green-700 font-bold">Présents</div>
              <div className="font-heading font-extrabold text-2xl text-green-700">{counts.present}</div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center" data-testid="stat-late">
              <div className="text-xs uppercase tracking-wider text-orange-700 font-bold">Retards</div>
              <div className="font-heading font-extrabold text-2xl text-orange-700">{counts.late}</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center" data-testid="stat-absent">
              <div className="text-xs uppercase tracking-wider text-red-700 font-bold">Absents</div>
              <div className="font-heading font-extrabold text-2xl text-red-700">{counts.absent}</div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-bold text-[#1E3A8A]">{students.length} élève(s) · {date}</h3>
              <Button onClick={submit} disabled={saving} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="attendance-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer l&apos;appel
              </Button>
            </div>
            <div className="divide-y divide-slate-100">
              {students.map((s) => {
                const st = statuses[s.id] || "present";
                return (
                  <div key={s.id} className="p-4 flex items-center justify-between hover:bg-slate-50" data-testid={`attendance-row-${s.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs ${s.gender === "M" ? "bg-[#1E3A8A]" : "bg-[#EA580C]"}`}>
                        {s.first_name[0]}{s.last_name[0]}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-[#0F172A]">{s.first_name} {s.last_name}</div>
                        <div className="text-xs text-slate-500">{s.gender === "M" ? "Garçon" : "Fille"}</div>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      {[
                        { v: "present", label: "Présent", color: "green", icon: Check },
                        { v: "late", label: "Retard", color: "orange", icon: Clock },
                        { v: "absent", label: "Absent", color: "red", icon: X },
                      ].map(opt => (
                        <button
                          key={opt.v}
                          onClick={() => setStatus(s.id, opt.v)}
                          className={`text-xs flex items-center gap-1 px-3 py-1.5 rounded-md font-semibold border transition ${
                            st === opt.v
                              ? `bg-${opt.color}-600 text-white border-${opt.color}-600`
                              : `bg-white text-${opt.color}-700 border-slate-200 hover:border-${opt.color}-300 hover:bg-${opt.color}-50`
                          }`}
                          data-testid={`attendance-${opt.v}-${s.id}`}
                          style={st === opt.v ? { backgroundColor: opt.color === "green" ? "#16a34a" : opt.color === "orange" ? "#ea580c" : "#dc2626", borderColor: opt.color === "green" ? "#16a34a" : opt.color === "orange" ? "#ea580c" : "#dc2626" } : {}}
                        >
                          <opt.icon className="h-3 w-3" /> {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
